import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Scan, CheckCircle, XCircle, History, Camera, AlertCircle, Bug, Keyboard } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MerchantScanner({ user, logout }) {
  const [scanResult, setScanResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [scanHistory, setScanHistory] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scannerError, setScannerError] = useState(null);
  const [cameraList, setCameraList] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [debugData, setDebugData] = useState(null);
  const [loadingDebug, setLoadingDebug] = useState(false);
  const [manualQrCode, setManualQrCode] = useState("");
  const html5QrCodeRef = useRef(null);

  const isAdmin = user?.role === "convenor" || user?.role === "captain";

  const testManualQrCode = async () => {
    if (!manualQrCode.trim()) {
      toast.error("Please enter a QR code string");
      return;
    }
    await verifyQRCode(manualQrCode.trim());
  };

  useEffect(() => {
    fetchScanHistory();
    
    // Cleanup on unmount
    return () => {
      stopScanner();
    };
  }, []);

  const fetchScanHistory = async () => {
    try {
      const response = await axios.get(`${API}/merchant/scan-history`, {
        withCredentials: true
      });
      setScanHistory(response.data);
    } catch (error) {
      console.error("Error fetching scan history:", error);
    }
  };

  const fetchDebugData = async () => {
    setLoadingDebug(true);
    try {
      const response = await axios.get(`${API}/merchant/debug-qr`, {
        withCredentials: true
      });
      setDebugData(response.data);
    } catch (error) {
      console.error("Error fetching debug data:", error);
      toast.error("Failed to load debug data");
    } finally {
      setLoadingDebug(false);
    }
  };

  const startScanner = async () => {
    setScannerError(null);
    setScanResult(null);
    
    try {
      // First, request camera permissions explicitly
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "environment" } 
      });
      // Stop the test stream
      stream.getTracks().forEach(track => track.stop());
      
      // Get available cameras
      const devices = await Html5Qrcode.getCameras();
      
      if (devices && devices.length > 0) {
        setCameraList(devices);
        // Prefer back camera
        const backCamera = devices.find(d => 
          d.label.toLowerCase().includes('back') || 
          d.label.toLowerCase().includes('rear') ||
          d.label.toLowerCase().includes('environment')
        );
        const cameraToUse = backCamera || devices[0];
        setSelectedCamera(cameraToUse.id);
        
        setIsScanning(true);
        
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          initializeScanner(cameraToUse.id);
        }, 100);
      } else {
        setScannerError("No cameras found on this device");
        toast.error("No cameras found on this device");
      }
    } catch (err) {
      console.error("Camera access error:", err);
      let errorMessage = "Unable to access camera. ";
      
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        errorMessage += "Please allow camera access in your browser settings.";
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        errorMessage += "No camera found on this device.";
      } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
        errorMessage += "Camera is in use by another application.";
      } else if (err.name === "OverconstrainedError") {
        errorMessage += "Camera constraints not satisfied.";
      } else if (err.name === "SecurityError") {
        errorMessage += "Camera access blocked. Make sure you're on HTTPS.";
      } else {
        errorMessage += err.message || "Please check your browser settings.";
      }
      
      setScannerError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const initializeScanner = async (cameraId) => {
    try {
      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;
      
      await html5QrCode.start(
        cameraId,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        onScanSuccess,
        onScanError
      );
      
      console.log("Scanner started successfully");
    } catch (err) {
      console.error("Scanner initialization error:", err);
      setScannerError("Failed to start scanner: " + err.message);
      setIsScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING state
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
      html5QrCodeRef.current = null;
    }
    setIsScanning(false);
  };

  const onScanSuccess = async (decodedText) => {
    console.log("QR Code scanned:", decodedText);
    await stopScanner();
    await verifyQRCode(decodedText);
  };

  const onScanError = (error) => {
    // Silent - don't log every frame without a QR code
  };

  const verifyQRCode = async (qrCode) => {
    setVerifying(true);
    try {
      const response = await axios.post(
        `${API}/merchant/verify-qr`,
        { qr_code: qrCode },
        { withCredentials: true }
      );

      setScanResult(response.data);

      if (response.data.valid) {
        toast.success("Valid QR code!");
      } else {
        toast.error(response.data.message || "Invalid QR code");
      }
    } catch (error) {
      console.error("Error verifying QR:", error);
      const errorDetail = error.response?.data?.detail || "Failed to verify QR code";
      const errorDebug = error.response?.data?.debug || null;
      toast.error(errorDetail);
      setScanResult({
        valid: false,
        message: errorDetail,
        debug: errorDebug || { 
          error_type: "http_error",
          status: error.response?.status,
          raw_error: error.message
        }
      });
    } finally {
      setVerifying(false);
    }
  };

  const markAsUsed = async () => {
    if (!scanResult || !scanResult.usage_id) return;

    try {
      await axios.post(
        `${API}/merchant/mark-used/${scanResult.usage_id}`,
        {},
        { withCredentials: true }
      );

      toast.success("Discount marked as used!");
      setScanResult(null);
      fetchScanHistory();
    } catch (error) {
      console.error("Error marking as used:", error);
      toast.error(error.response?.data?.detail || "Failed to mark as used");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/dashboard">
            <Button variant="ghost" className="text-yellow-400 hover:text-yellow-300" data-testid="back-button">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-yellow-400 uppercase" style={{ fontFamily: "'Times New Roman', Times, Georgia, serif", letterSpacing: "0.05em" }}>SCC Football</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <h2 className="text-4xl font-bold text-white mb-8" data-testid="page-title">
          Merchant Scanner
        </h2>

        <Tabs defaultValue="scanner" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="scanner" data-testid="scanner-tab">
              <Scan className="w-4 h-4 mr-2" />
              Scanner
            </TabsTrigger>
            <TabsTrigger value="manual" data-testid="manual-tab">
              <Keyboard className="w-4 h-4 mr-2" />
              Manual
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="history-tab">
              <History className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="debug" data-testid="debug-tab" onClick={fetchDebugData}>
                <Bug className="w-4 h-4 mr-2" />
                Debug
              </TabsTrigger>
            )}
          </TabsList>

          {/* Scanner Tab */}
          <TabsContent value="scanner">
            <div className="max-w-2xl mx-auto">
              {/* Scanner */}
              <Card className="p-8 bg-slate-900/50 shadow-lg mb-6">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500/20 rounded-full mb-4">
                    <Camera className="w-8 h-8 text-yellow-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Scan Member QR Code</h3>
                  <p className="text-slate-400">
                    Position the QR code within the camera frame to verify the discount
                  </p>
                </div>

                {/* Error Message */}
                {scannerError && (
                  <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg" data-testid="scanner-error">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-red-400 font-medium">Camera Error</p>
                        <p className="text-red-300 text-sm mt-1">{scannerError}</p>
                        <p className="text-slate-400 text-xs mt-2">
                          <strong>Brave Browser Users:</strong> Click the shield icon in the address bar and allow camera access, or go to Settings → Privacy → Site Settings → Camera.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {!isScanning && !scanResult && (
                  <Button
                    onClick={startScanner}
                    className="w-full bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold py-6 text-lg"
                    data-testid="start-scan-button"
                  >
                    <Scan className="w-6 h-6 mr-2" />
                    {scannerError ? "Try Again" : "Start Scanner"}
                  </Button>
                )}

                {isScanning && (
                  <div>
                    <div 
                      id="qr-reader" 
                      className="mb-4 overflow-hidden rounded-lg bg-slate-800"
                      style={{ minHeight: "300px" }}
                    >
                      <div className="flex items-center justify-center h-[300px] text-slate-400">
                        <div className="text-center">
                          <div className="spinner mx-auto mb-2"></div>
                          <p>Initializing camera...</p>
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={stopScanner}
                      variant="outline"
                      className="w-full border-slate-600 text-slate-300 hover:bg-slate-800"
                      data-testid="stop-scan-button"
                    >
                      Cancel Scanning
                    </Button>
                  </div>
                )}

                {verifying && (
                  <div className="text-center py-8">
                    <div className="spinner mx-auto mb-4"></div>
                    <p className="text-slate-400">Verifying QR code...</p>
                  </div>
                )}
              </Card>

              {/* Scan Result */}
              {scanResult && !verifying && (
                <Card className={`p-8 ${scanResult.valid ? 'bg-emerald-500/10 border-2 border-emerald-500/30' : 'bg-red-500/10 border-2 border-red-400'}`} data-testid="scan-result">
                  <div className="text-center mb-6">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${scanResult.valid ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                      {scanResult.valid ? (
                        <CheckCircle className="w-10 h-10 text-emerald-400" />
                      ) : (
                        <XCircle className="w-10 h-10 text-red-400" />
                      )}
                    </div>
                    <h3 className={`text-2xl font-bold mb-2 ${scanResult.valid ? 'text-green-300' : 'text-red-300'}`}>
                      {scanResult.valid ? "Valid QR Code!" : "Invalid QR Code"}
                    </h3>
                    <p className={scanResult.valid ? 'text-emerald-400' : 'text-red-400'}>
                      {scanResult.message || (scanResult.valid ? "Discount can be applied" : "Cannot be used")}
                    </p>
                  </div>

                  {scanResult.valid && scanResult.discount && (
                    <div className="space-y-4 mb-6">
                      <div className="bg-slate-900/50 p-4 rounded-lg">
                        <h4 className="font-bold text-white mb-3 text-lg">Discount Details</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-slate-400">Offer:</span>
                            <span className="font-semibold text-white" data-testid="discount-title">{scanResult.discount.title}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Discount:</span>
                            <span className="font-semibold text-yellow-500" data-testid="discount-percentage">
                              {scanResult.discount.discount_percentage}% OFF
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Code:</span>
                            <span className="font-mono font-semibold text-white" data-testid="discount-code">
                              {scanResult.discount.discount_code}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-slate-900/50 p-4 rounded-lg">
                        <h4 className="font-bold text-white mb-3 text-lg">Member Details</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-slate-400">Name:</span>
                            <span className="font-semibold text-white" data-testid="member-name">{scanResult.member.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Membership #:</span>
                            <span className="font-semibold text-white" data-testid="member-number">
                              {scanResult.member.membership_number}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Type:</span>
                            <span className={`font-semibold capitalize ${scanResult.member.member_type === 'section' ? 'text-yellow-500' : 'text-white'}`} data-testid="member-type">
                              {scanResult.member.member_type === 'section' ? 'Section Member ⭐' : 'Non-Section Member'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Status:</span>
                            <span className="font-semibold text-emerald-400 capitalize" data-testid="member-status">
                              {scanResult.member.status}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Button
                          onClick={markAsUsed}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-6 text-lg"
                          data-testid="confirm-button"
                        >
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Confirm & Apply Discount
                        </Button>
                        <Button
                          onClick={() => {
                            setScanResult(null);
                            startScanner();
                          }}
                          variant="outline"
                          className="px-6"
                          data-testid="scan-another-button"
                        >
                          Scan Another
                        </Button>
                      </div>
                    </div>
                  )}

                  {!scanResult.valid && (
                    <Button
                      onClick={() => {
                        setScanResult(null);
                        startScanner();
                      }}
                      className="w-full bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold"
                      data-testid="try-again-button"
                    >
                      Try Again
                    </Button>
                  )}
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Manual Input Tab */}
          <TabsContent value="manual">
            <div className="max-w-2xl mx-auto">
              <Card className="p-8 bg-slate-900/50 shadow-lg mb-6">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500/20 rounded-full mb-4">
                    <Keyboard className="w-8 h-8 text-yellow-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Manual QR Code Entry</h3>
                  <p className="text-slate-400">
                    Paste the QR code string to test verification (for debugging)
                  </p>
                </div>

                <div className="space-y-4">
                  <Input
                    placeholder="Paste QR code string here (e.g., DISCOUNT-abc123-...)"
                    value={manualQrCode}
                    onChange={(e) => setManualQrCode(e.target.value)}
                    className="font-mono text-sm"
                    data-testid="manual-qr-input"
                  />
                  <Button
                    onClick={testManualQrCode}
                    disabled={verifying || !manualQrCode.trim()}
                    className="w-full bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold py-4"
                    data-testid="test-qr-button"
                  >
                    {verifying ? "Verifying..." : "Test QR Code"}
                  </Button>
                </div>

                {/* Show what was sent */}
                {manualQrCode && (
                  <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-xs text-slate-400 mb-1">QR Code String (length: {manualQrCode.length}):</p>
                    <p className="text-xs text-slate-300 font-mono break-all">{manualQrCode}</p>
                  </div>
                )}
              </Card>

              {/* Show scan result here too */}
              {scanResult && !verifying && (
                <Card className={`p-8 ${scanResult.valid ? 'bg-emerald-500/10 border-2 border-emerald-500/30' : 'bg-red-500/10 border-2 border-red-400'}`}>
                  <div className="text-center mb-4">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${scanResult.valid ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                      {scanResult.valid ? (
                        <CheckCircle className="w-10 h-10 text-emerald-400" />
                      ) : (
                        <XCircle className="w-10 h-10 text-red-400" />
                      )}
                    </div>
                    <h3 className={`text-2xl font-bold mb-2 ${scanResult.valid ? 'text-green-300' : 'text-red-300'}`}>
                      {scanResult.valid ? "Valid QR Code!" : "Invalid QR Code"}
                    </h3>
                    <p className={scanResult.valid ? 'text-emerald-400' : 'text-red-400'}>
                      {scanResult.message || (scanResult.valid ? "Discount can be applied" : "Cannot be used")}
                    </p>
                    
                    {/* Debug info for invalid results */}
                    {!scanResult.valid && scanResult.debug && (
                      <div className="mt-4 p-3 bg-slate-900/50 rounded text-left">
                        <p className="text-xs text-slate-400 font-bold mb-2">Debug Info:</p>
                        <pre className="text-xs text-slate-300 whitespace-pre-wrap">
                          {JSON.stringify(scanResult.debug, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card className="p-6 bg-slate-900/50 shadow-lg">
              <h3 className="text-2xl font-bold text-white mb-6">Scan History</h3>

              {scanHistory.length === 0 ? (
                <p className="text-center text-slate-400 py-8" data-testid="no-history">
                  No scans yet. Start scanning member QR codes!
                </p>
              ) : (
                <div className="space-y-4">
                  {scanHistory.map((record, index) => (
                    <Card
                      key={record.id || index}
                      className="p-4 border-2 border-white/5"
                      data-testid={`history-item-${index}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-bold text-white mb-1" data-testid={`history-discount-${index}`}>
                            {record.discount_title}
                          </h4>
                          <p className="text-sm text-slate-400 mb-2" data-testid={`history-member-${index}`}>
                            Member: {record.member_name} ({record.membership_number})
                          </p>
                          <p className="text-xs text-slate-400" data-testid={`history-date-${index}`}>
                            {new Date(record.used_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center text-emerald-400">
                          <CheckCircle className="w-5 h-5" />
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Debug Tab - Admin Only */}
          {isAdmin && (
            <TabsContent value="debug">
              <Card className="p-6 bg-slate-900/50 shadow-lg">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-white">QR Code Debug Info</h3>
                  <Button 
                    onClick={fetchDebugData} 
                    variant="outline"
                    disabled={loadingDebug}
                  >
                    {loadingDebug ? "Loading..." : "Refresh"}
                  </Button>
                </div>

                {!debugData ? (
                  <p className="text-center text-slate-400 py-8">
                    Click Refresh to load debug data
                  </p>
                ) : (
                  <div className="space-y-6">
                    {/* Summary */}
                    <div className="grid grid-cols-2 gap-4">
                      <Card className="p-4 bg-slate-800/50">
                        <p className="text-sm text-slate-400">Total QR Codes Generated</p>
                        <p className="text-2xl font-bold text-white">{debugData.total_qr_codes}</p>
                      </Card>
                      <Card className="p-4 bg-slate-800/50">
                        <p className="text-sm text-slate-400">Total Discounts</p>
                        <p className="text-2xl font-bold text-white">{debugData.all_discounts?.length || 0}</p>
                      </Card>
                    </div>

                    {/* All Discounts */}
                    <div>
                      <h4 className="font-bold text-white mb-3">All Discounts in System</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {debugData.all_discounts?.map((d, i) => (
                          <div key={i} className="p-2 bg-slate-800/30 rounded text-sm">
                            <span className={d.is_active ? "text-emerald-400" : "text-red-400"}>
                              {d.is_active ? "✓" : "✗"}
                            </span>
                            <span className="text-white ml-2">{d.title}</span>
                            <span className="text-slate-500 ml-2 text-xs font-mono">{d.id?.slice(0, 8)}...</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* QR Codes */}
                    <div>
                      <h4 className="font-bold text-white mb-3">Generated QR Codes</h4>
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {debugData.qr_codes?.map((qr, i) => (
                          <Card 
                            key={i} 
                            className={`p-3 border ${qr.discount_found ? 'border-slate-700' : 'border-red-500/50 bg-red-500/10'}`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="text-white font-medium">
                                  {qr.discount_title}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                  Member: {qr.membership_number}
                                </p>
                                <div className="mt-2 p-2 bg-slate-800/50 rounded">
                                  <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs text-slate-400">Full QR Code ({qr.qr_code_length} chars):</span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 px-2 text-xs text-yellow-400 hover:text-yellow-300"
                                      onClick={() => {
                                        navigator.clipboard.writeText(qr.qr_code);
                                        toast.success("QR code copied to clipboard!");
                                      }}
                                    >
                                      Copy
                                    </Button>
                                  </div>
                                  <p className="text-xs text-slate-300 font-mono break-all select-all">
                                    {qr.qr_code}
                                  </p>
                                </div>
                                <p className="text-xs text-slate-500 mt-2">
                                  Discount ID: {qr.discount_id}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                  Created: {new Date(qr.created_at).toLocaleString()}
                                </p>
                              </div>
                              <div className="text-right ml-3">
                                {!qr.discount_found ? (
                                  <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">
                                    DISCOUNT MISSING
                                  </span>
                                ) : qr.used_at ? (
                                  <span className="text-xs bg-slate-500/20 text-slate-400 px-2 py-1 rounded">
                                    Used
                                  </span>
                                ) : (
                                  <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">
                                    Valid
                                  </span>
                                )}
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
